import { Injectable, runInInjectionContext, EnvironmentInjector } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Auth, onAuthStateChanged, signInAnonymously, signInWithPopup, GoogleAuthProvider, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as webSignOut, User as FirebaseUser, signInWithCredential, EmailAuthProvider, linkWithCredential, updateProfile, OAuthProvider } from '@angular/fire/auth';
import { Firestore, doc, setDoc, getDoc, updateDoc, SetOptions } from '@angular/fire/firestore';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { User as AppUser } from '../entities/user.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {


  private currentUserSubject: BehaviorSubject<AppUser | null> = new BehaviorSubject<AppUser | null>(null);
  public currentUser$: Observable<AppUser | null> = this.currentUserSubject.asObservable();

  private authStateResolvedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public authStateResolved$: Observable<boolean> = this.authStateResolvedSubject.asObservable();

  private hasReconciled = false;

  constructor(private platform: Platform, private auth: Auth, private firestore: Firestore, private envInjector: EnvironmentInjector) {

    onAuthStateChanged(this.auth, async (firebaseUser) => {
      console.log('[AuthDebug] onAuthStateChanged fired', {
        jsUid: firebaseUser?.uid || null,
        isNative: Capacitor.isNativePlatform()
      });

      // After initial reconcile, prevent phantom JS sessions on native
      if (Capacitor.isNativePlatform() && this.hasReconciled) {
        try {
          const native = await FirebaseAuthentication.getCurrentUser();
          const nativeUid = (native as any)?.user?.uid || (native as any)?.uid || null;
          const jsUid = firebaseUser?.uid || null;
          if (!nativeUid && jsUid) {
            // Keep web session if JS has a user (anonymous or not). Native may be cleared or delayed.
            console.log('[AuthDebug] onAuthStateChanged: native null, JS user present -> keeping web session');
          }
          if (nativeUid && jsUid && nativeUid !== jsUid) {
            console.log('[AuthDebug] onAuthStateChanged: mismatch native/js -> signing out web user');
            try { await webSignOut(this.auth); } catch (e) { console.warn('[AuthDebug] onAuthStateChanged webSignOut error', e); }
            this.currentUserSubject.next(null);
            return;
          }
        } catch (e) {
          console.warn('[AuthDebug] onAuthStateChanged native check error', e);
        }
      }

      if (firebaseUser) {
        await this.getCurrentUser();
      } else {
        this.currentUserSubject.next(null);
      }
    });

    void (async () => {
      await this.platform.ready();
      await this.reconcileAuthState();
      this.hasReconciled = true;
      this.authStateResolvedSubject.next(true);
      console.log('[AuthDebug] startup reconcile complete, authStateResolved=true');
    })();
  }

  // Public debug helper for startup or manual checks
  public async debugAuthSnapshot(tag: string): Promise<void> {
    const isNative = Capacitor.isNativePlatform();
    let nativeUid: string | null = null;
    try {
      if (isNative) {
        const native = await FirebaseAuthentication.getCurrentUser();
        nativeUid = (native as any)?.user?.uid || (native as any)?.uid || null;
      }
    } catch (e) {
      console.warn('[AuthDebug] native getCurrentUser error', e);
    }
    const jsUser = this.auth.currentUser;
    let tokenInfo: any = null;
    if (jsUser) {
      try {
        const token = await jsUser.getIdToken();
        tokenInfo = { hasToken: !!token, preview: token?.slice(0, 12) };
      } catch (e) {
        tokenInfo = { error: (e as any)?.code || (e as any)?.message };
      }
    }
    console.log('[AuthDebug] snapshot', tag, {
      isNative,
      nativeUid,
      jsUid: jsUser?.uid || null,
      tokenInfo
    });
  }

  // Reconcile JS SDK vs Native auth state on app start. Prefer Native state on device.
  private async reconcileAuthState(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('[AuthDebug] reconcile skipped (web)');
      return;
    }
    let nativeUid: string | null = null;
    try {
      const native = await FirebaseAuthentication.getCurrentUser();
      nativeUid = (native as any)?.user?.uid || (native as any)?.uid || null;
    } catch (e) {
      console.warn('[AuthDebug] reconcile native getCurrentUser error', e);
    }
    const jsUser = this.auth.currentUser;
    const jsUid = jsUser?.uid || null;
    console.log('[AuthDebug] reconcile start', { nativeUid, jsUid });

    // Case 1: Native signed out, JS signed in -> sign out JS to avoid phantom session
    if (!nativeUid && jsUid) {
      // Allow JS-only anonymous sessions to persist across restarts
      if (jsUser?.isAnonymous) {
        console.log('[AuthDebug] reconcile: native null + JS anonymous -> allowing JS-only session');
      } else {
        console.log('[AuthDebug] reconcile: native null, web has user -> webSignOut');
        try { await webSignOut(this.auth); } catch (e) { console.warn('[AuthDebug] reconcile webSignOut error', e); }
        this.currentUserSubject.next(null);
        return;
      }
    }

    // Case 2: Native signed in, JS not signed in -> wait briefly for bridge instead of clearing native
    if (nativeUid && !jsUid) {
      console.log('[AuthDebug] reconcile: native has user, web null -> waiting for JS bridge');
      try {
        await this.waitForBridgedUser(nativeUid, 5000);
        console.log('[AuthDebug] reconcile: JS bridged to native');
      } catch (e) {
        console.warn('[AuthDebug] reconcile: bridge not detected, proceeding without forcing sign-outs');
      }
    }

    // Case 3: Both present but mismatch -> prefer native by clearing web
    if (nativeUid && jsUid && nativeUid !== jsUid) {
      console.log('[AuthDebug] reconcile: mismatch -> clearing web session');
      try { await webSignOut(this.auth); } catch (e) { console.warn('[AuthDebug] reconcile webSignOut error', e); }
      this.currentUserSubject.next(null);
      return;
    }

    console.log('[AuthDebug] reconcile: states aligned');
  }

  async signInAnonymously(nativeLang: string): Promise<FirebaseUser | null> {
    console.log('[AuthDebug] signInAnonymously: entry', { nativeLang, isNative: Capacitor.isNativePlatform() });
    if (Capacitor.isNativePlatform()) {
      console.log('[AuthDebug] signInAnonymously: native platform detected, starting native-safe flow');
      // Ensure native session is cleared to avoid conflicts
      try {
        await FirebaseAuthentication.signOut();
        console.log('[AuthDebug] signInAnonymously: native signOut done');
      } catch (e) {
        console.warn('[AuthDebug] signInAnonymously: native signOut error', e);
      }
      // Ensure JS session is cleared too
      if (this.auth.currentUser) {
        try {
          await webSignOut(this.auth);
          console.log('[AuthDebug] signInAnonymously: web signOut done');
        } catch (e) {
          console.warn('[AuthDebug] signInAnonymously: web signOut error', e);
        }
      }
      // Wait until JS user is null to avoid race with onAuthStateChanged
      console.log('[AuthDebug] signInAnonymously: waiting for no JS user');
      await this.waitForNoJsUser();
      console.log('[AuthDebug] signInAnonymously: no JS user, proceeding to web signInAnonymously');
      // Now perform web anonymous sign-in so JS is the source of truth
      let credential: any;
      try {
        credential = await runInInjectionContext(this.envInjector, () => signInAnonymously(this.auth));
        console.log('[AuthDebug] signInAnonymously: web signInAnonymously credential', credential && !!credential.user, credential);
      } catch (e) {
        console.warn('[AuthDebug] signInAnonymously: web signInAnonymously error', e);
        return null;
      }
      if (credential && credential.user) {
        try {
          await this.waitForAuthToken();
          await this.createUserDocument({ uid: credential.user.uid, email: null, displayName: '' }, nativeLang);
          console.log('[AuthDebug] signInAnonymously: user document created', credential.user.uid);
        } catch (e) {
          console.warn('[AuthDebug] signInAnonymously: createUserDocument error', e);
        }
        await this.debugAuthSnapshot('after-anon-signin');
        console.log('[AuthDebug] signInAnonymously: returning user', credential.user.uid);
        return credential.user;
      }
      console.log('[AuthDebug] signInAnonymously: credential.user missing, returning null');
      return null;
    } else {
      console.log('[AuthDebug] signInAnonymously: web platform detected, starting web flow');
      let credential: any;
      try {
        credential = await runInInjectionContext(this.envInjector, () => signInAnonymously(this.auth));
        console.log('[AuthDebug] signInAnonymously: web signInAnonymously credential', credential && !!credential.user, credential);
      } catch (e) {
        console.warn('[AuthDebug] signInAnonymously: web signInAnonymously error', e);
        return null;
      }
      if (credential && credential.user) {
        try {
          await this.waitForAuthToken();
          await this.createUserDocument({ uid: credential.user.uid, email: null, displayName: '' }, nativeLang);
          console.log('[AuthDebug] signInAnonymously: user document created', credential.user.uid);
        } catch (e) {
          console.warn('[AuthDebug] signInAnonymously: createUserDocument error', e);
        }
        await this.debugAuthSnapshot('after-anon-signin');
        console.log('[AuthDebug] signInAnonymously: returning user', credential.user.uid);
        return credential.user;
      }
      console.log('[AuthDebug] signInAnonymously: credential.user missing, returning null');
      return null;
    }
  }

  async signInWithGoogle(nativeLang: string = ''): Promise<FirebaseUser | null> {
    if (Capacitor.isNativePlatform()) {
      const result: any = await FirebaseAuthentication.signInWithGoogle();
      const nativeUser = result?.user;
      const idToken: string | undefined = result?.credential?.idToken;
      const accessToken: string | undefined = result?.credential?.accessToken;

      // Prefer manual JS sign-in when idToken is provided by the native plugin
      if (idToken) {
        try {
          const googleCred = GoogleAuthProvider.credential(idToken);
          const jsCredential = await runInInjectionContext(this.envInjector, () => signInWithCredential(this.auth, googleCred));
          const jsUser = jsCredential.user;
          await this.waitForAuthToken();
          await this.debugAuthSnapshot('after-native-google-manual-js-signin');
          await this.ensureUserDoc(jsUser.uid, { email: jsUser.email, displayName: jsUser.displayName || '', nativeLang });
          return jsUser;
        } catch (e) {
          console.warn('[AuthService] Manual JS sign-in with Google credential failed, falling back to bridge', e);
        }
      }

      // Fallback: rely on bridge if no credential returned (or manual sign-in failed)
      if (nativeUser) {
        await this.waitForBridgedUser(nativeUser.uid);
        await this.debugAuthSnapshot('after-native-google-bridge');
        await this.ensureUserDoc(nativeUser.uid, { email: nativeUser.email, displayName: nativeUser.displayName || '', nativeLang });
        return this.auth.currentUser!;
      }
      return null;
    } else {
      const provider = new GoogleAuthProvider();
      try {
        console.log('[AuthDebug] signInWithGoogle: web flow - invoking signInWithPopup');
        const credential = await runInInjectionContext(this.envInjector, () => signInWithPopup(this.auth, provider));
        console.log('[AuthDebug] signInWithGoogle: web flow - popup returned', { hasUser: !!credential?.user });
        if (credential.user) {
          await this.createUserDocument({ ...credential.user, displayName: credential.user.displayName || '' }, nativeLang);
          return credential.user;
        }
        return null;
      } catch (e) {
        console.error('[AuthService] signInWithGoogle (web) failed', e);
        throw e;
      }
    }
  }

  async signInWithApple(nativeLang: string = ''): Promise<{ user: FirebaseUser; isNewUser: boolean } | null> {
    console.log('[AuthDebug] signInWithApple: entry', { nativeLang, isNative: Capacitor.isNativePlatform() });
    
    if (Capacitor.isNativePlatform()) {
      try {
        // Use skipNativeAuth to bypass bridge issues and use JS SDK directly
        const result: any = await FirebaseAuthentication.signInWithApple({
          skipNativeAuth: true
        });
        const nativeUser = result?.user;
        const idToken: string | undefined = result?.credential?.idToken;
        const rawNonce: string | undefined = result?.credential?.nonce;

                console.log('[AuthDebug] signInWithApple: native result', {
          hasUser: !!nativeUser,
          hasIdToken: !!idToken,
          hasNonce: !!rawNonce
        });

        // With skipNativeAuth: true, the JS SDK is used directly, so we can try manual credential creation
        if (idToken && rawNonce) {
          try {
            console.log('[AuthDebug] signInWithApple: attempting manual JS credential creation with skipNativeAuth');
            const provider = new OAuthProvider('apple.com');
            const appleCredential = provider.credential({ idToken, rawNonce });
            const jsCredential = await runInInjectionContext(this.envInjector, () => signInWithCredential(this.auth, appleCredential));
            const jsUser = jsCredential.user;
            await this.waitForAuthToken();
            await this.debugAuthSnapshot('after-native-apple-manual-js-signin');
            const isNewUser = await this.ensureUserDoc(jsUser.uid, {
              email: jsUser.email,
              displayName: jsUser.displayName || '',
              nativeLang
            });
            console.log('[AuthDebug] signInWithApple: manual JS sign-in successful', jsUser.uid);
            return { user: jsUser, isNewUser };
          } catch (e) {
            console.warn('[AuthService] Manual JS sign-in with Apple credential failed', e);
          }
        }

        // Fallback: the JS SDK should have authenticated the user directly
        if (this.auth.currentUser) {
          console.log('[AuthDebug] signInWithApple: using current JS user');
          await this.debugAuthSnapshot('after-native-apple-js-fallback');
          const isNewUser = await this.ensureUserDoc(this.auth.currentUser.uid, {
            email: this.auth.currentUser.email,
            displayName: this.auth.currentUser.displayName || '',
            nativeLang
          });
          return { user: this.auth.currentUser, isNewUser };
        }
        
        console.log('[AuthDebug] signInWithApple: no user returned from native');
        return null;
      } catch (error) {
        console.error('[AuthService] Apple Sign-In failed:', error);
        throw error;
      }
    } else {
      // Web implementation
      try {
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        
        const credential = await runInInjectionContext(this.envInjector, () => signInWithPopup(this.auth, provider));
        if (credential.user) {
          const isNewUser = await this.createUserDocument({ 
            ...credential.user, 
            displayName: credential.user.displayName || '' 
          }, nativeLang);
          console.log('[AuthDebug] signInWithApple: web sign-in successful', credential.user.uid);
          return { user: credential.user, isNewUser };
        }
        return null;
      } catch (error) {
        console.error('[AuthService] Apple Sign-In (web) failed:', error);
        throw error;
      }
    }
  }

  async signInWithEmail(email: string, password: string): Promise<FirebaseUser | null> {
    if (Capacitor.isNativePlatform()) {
      const { user } = await FirebaseAuthentication.signInWithEmailAndPassword({ email, password });
      if (user) {
        await this.bridgeWebAuthIfNeeded(user.uid, async () => {
          await runInInjectionContext(this.envInjector, () => signInWithEmailAndPassword(this.auth, email, password));
        });
        await this.waitForAuthToken();
        await this.debugAuthSnapshot('after-native-email-signin-bridge');
        const userDocRef = doc(this.firestore, `users/${user.uid}`);
        const userSnap = await this.safeGetDoc(userDocRef);
        if (userSnap?.exists()) {
          this.currentUserSubject.next(userSnap.data() as AppUser);
        }
        return this.auth.currentUser!;
      }
      return null;
    } else {
      const credential = await runInInjectionContext(this.envInjector, () => signInWithEmailAndPassword(this.auth, email, password));
      if (credential.user) {
        const userDoc = doc(this.firestore, `users/${credential.user.uid}`);
        const userSnap = await getDoc(userDoc);
        if (userSnap.exists()) {
          const userData = userSnap.data() as AppUser;
          this.currentUserSubject.next(userData);
        }
      }
      return credential.user;
    }
  }

  async signUpWithEmail(email: string, password: string, displayName: string, nativeLang: string): Promise<FirebaseUser | null> {
    try {
      console.debug('[AuthService] Attempting sign up with email:', email, 'displayName:', displayName, 'nativeLang:', nativeLang);
      if (Capacitor.isNativePlatform()) {
        const { user } = await FirebaseAuthentication.createUserWithEmailAndPassword({ email, password });
        console.debug('[AuthService] Native sign up result:', user);
        if (user) {
          console.debug('[AuthService] Ensuring JS SDK sign-in for UID:', user.uid);
          await this.bridgeWebAuthIfNeeded(user.uid, async () => {
            await runInInjectionContext(this.envInjector, () => signInWithEmailAndPassword(this.auth, email, password));
          });
          await this.waitForAuthToken();
          await this.debugAuthSnapshot('after-native-email-signup-bridge');
          console.debug('[AuthService] Creating user document for UID:', user.uid);
          await this.ensureUserDoc(user.uid, { email, displayName, nativeLang });
          console.debug('[AuthService] User document created successfully for UID:', user.uid);
          return this.auth.currentUser!;
        }
        return null;
      } else {
        const credential = await runInInjectionContext(this.envInjector, () => createUserWithEmailAndPassword(this.auth, email, password));
        console.debug('[AuthService] Web sign up credential:', credential);
        if (credential.user) {
          console.debug('[AuthService] Creating user document for UID:', credential.user.uid);
          await this.createUserDocument({ uid: credential.user.uid, email, displayName }, nativeLang);
          console.debug('[AuthService] User document created successfully for UID:', credential.user.uid);
          return credential.user;
        } else {
          console.warn('[AuthService] No user returned from createUserWithEmailAndPassword');
        }
        return null;
      }
    } catch (error) {
      console.error('[AuthService] Error during signUpWithEmail:', error);
      throw error;
    }
  }

  // **Convert Anonymous User to Email Account**
  async convertAnonymousToEmail(email: string, password: string, displayName: string): Promise<FirebaseUser | null> {
    const currentUser = this.auth.currentUser;
    
    if (!currentUser || !currentUser.isAnonymous) {
      throw new Error('No anonymous user to convert');
    }

    try {
      // Create email credential
      const credential = EmailAuthProvider.credential(email, password);
      
      // Link the anonymous account with email/password
      const result = await runInInjectionContext(this.envInjector, () => linkWithCredential(currentUser, credential));
      
      // Update the user's profile with display name
      if (displayName) {
        await runInInjectionContext(this.envInjector, () => updateProfile(result.user, { displayName }));
      }
      
      // Update the user document in Firestore
      await this.updateUserDocument(result.user.uid, {
        email: email,
        displayName: displayName
      });
      
      // Refresh the current user data
      await this.getCurrentUser();
      
      return result.user;
    } catch (error) {
      console.error('Error converting anonymous user:', error);
      throw error;
    }
  }

  // **Convert Anonymous User to Google Account**
  async convertAnonymousToGoogle(): Promise<FirebaseUser | null> {
    const currentUser = this.auth.currentUser;
    
    if (!currentUser || !currentUser.isAnonymous) {
      throw new Error('No anonymous user to convert');
    }

    try {
      const provider = new GoogleAuthProvider();
      const result = await runInInjectionContext(this.envInjector, () => signInWithPopup(this.auth, provider));
      const credential = GoogleAuthProvider.credentialFromResult(result);
      
      if (!credential || !credential.accessToken) {
        throw new Error('Failed to get Google credential');
      }
      
      // Create a new credential and link it
      const googleCredential = GoogleAuthProvider.credential(credential.idToken, credential.accessToken);
      const linkResult = await runInInjectionContext(this.envInjector, () => linkWithCredential(currentUser, googleCredential));
      
      // Update the user document in Firestore
      await this.updateUserDocument(linkResult.user.uid, {
        email: linkResult.user.email || '',
        displayName: linkResult.user.displayName || '',
        photoURL: linkResult.user.photoURL || ''
      });
      
      // Refresh the current user data
      await this.getCurrentUser();
      
      return linkResult.user;
    } catch (error) {
      console.error('Error converting anonymous user to Google:', error);
      throw error;
    }
  }

  // **Convert Anonymous User to Apple Account**
  async convertAnonymousToApple(): Promise<FirebaseUser | null> {
    const currentUser = this.auth.currentUser;
    
    if (!currentUser || !currentUser.isAnonymous) {
      throw new Error('No anonymous user to convert');
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // Native platform - use skipNativeAuth to bypass bridge issues
        const result: any = await FirebaseAuthentication.signInWithApple({
          skipNativeAuth: true
        });
        const idToken: string | undefined = result?.credential?.idToken;
        const rawNonce: string | undefined = result?.credential?.nonce;
        
        if (!idToken) {
          throw new Error('Failed to get Apple credential');
        }

        // Try to link using the Apple credential
        try {
          const provider = new OAuthProvider('apple.com');
          const appleCredential = provider.credential({ idToken, rawNonce });
          const linkResult = await runInInjectionContext(this.envInjector, () => linkWithCredential(currentUser, appleCredential));

          // Update the user document in Firestore
          await this.updateUserDocument(linkResult.user.uid, {
            email: linkResult.user.email || '',
            displayName: linkResult.user.displayName || '',
            photoURL: linkResult.user.photoURL || ''
          });

          // Refresh the current user data
          await this.getCurrentUser();

          return linkResult.user;
        } catch (linkError) {
          console.warn('[AuthService] Apple credential linking failed, trying custom token approach', linkError);
          // If credential linking fails, try custom token approach
          const tokenResult = await FirebaseAuthentication.getIdToken();
          const customToken = tokenResult?.token;
          if (customToken) {
            // For linking, we still need to use the original credential approach
            // The custom token approach works better for initial sign-in than linking
            throw linkError; // Re-throw the original error
          } else {
            throw linkError;
          }
        }
      } else {
        // Web platform
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        
        const result = await runInInjectionContext(this.envInjector, () => signInWithPopup(this.auth, provider));
        const credential = OAuthProvider.credentialFromResult(result);
        
        if (!credential) {
          throw new Error('Failed to get Apple credential');
        }
        
        const linkResult = await runInInjectionContext(this.envInjector, () => linkWithCredential(currentUser, credential));
        
        // Update the user document in Firestore
        await this.updateUserDocument(linkResult.user.uid, {
          email: linkResult.user.email || '',
          displayName: linkResult.user.displayName || '',
          photoURL: linkResult.user.photoURL || ''
        });
        
        // Refresh the current user data
        await this.getCurrentUser();
        
        return linkResult.user;
      }
    } catch (error) {
      console.error('Error converting anonymous user to Apple:', error);
      throw error;
    }
  }

  // **Sign Out**
  async signOut(): Promise<void> {
    console.log('[AuthDebug] signOut start');
    await this.debugAuthSnapshot('before-signout');
    if (Capacitor.isNativePlatform()) {
      console.log('[AuthDebug] native signOut start');
      try { await FirebaseAuthentication.signOut(); } catch (e) { console.warn('[AuthDebug] native signOut error', e); }
      try { await webSignOut(this.auth); } catch (e) { console.warn('[AuthDebug] web signOut error', e); }
    } else {
      await webSignOut(this.auth);
    }
    this.currentUserSubject.next(null);
    await this.debugAuthSnapshot('after-signout');
    console.log('[AuthDebug] signOut end');
  }

  getUser(uid: string): Observable<AppUser | null> {
    const userDoc = doc(this.firestore, `users/${uid}`);
    return from(getDoc(userDoc).then(snapshot => snapshot.exists() ? snapshot.data() as AppUser : null));
  }

  setUser(user: AppUser) {
    this.currentUserSubject.next(user);
  }

  async getCurrentUser(): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    console.log('[AuthDebug] getCurrentUser entry', { jsUid: firebaseUser?.uid || null });
    if (firebaseUser) {
      const userDoc = doc(this.firestore, `users/${firebaseUser.uid}`);
      try {
        const userSnap = await this.safeGetDoc(userDoc);
        if (userSnap?.exists()) {
          const userData = userSnap.data() as AppUser;
          this.currentUserSubject.next(userData);
          console.log('[AuthDebug] getCurrentUser found user doc');
        } else {
          this.currentUserSubject.next(null);
          console.log('[AuthDebug] getCurrentUser no user doc');
        }
      } catch (error) {
        console.warn('getCurrentUser read warning:', error);
        this.currentUserSubject.next(null);
      }
    } else {
      this.currentUserSubject.next(null);
      console.log('[AuthDebug] getCurrentUser no js user');
    }
  }

  get currentUserValue(): AppUser | null {
    return this.currentUserSubject.value;
  }

  async createUserDocument(user: Partial<FirebaseUser & { displayName: string }>, nativeLang: string): Promise<boolean> {
    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    const snap = await this.safeGetDoc(userDocRef);
    if (!snap || !snap.exists()) {
      const userData = {
        uid: user.uid,
        email: user.email ?? null,
        displayName: user.displayName || '',
        nativeLanguage: nativeLang || 'en',
        targetLanguage: '',
        level: {},
        photoURL: (user as any).photoURL || '',
        bookmarks: [],
        completedTour: false,
        audioSpeed: 2,
        femaleVoice: true
      };
      await this.safeSetDoc(userDocRef, userData, true);
      this.currentUserSubject.next(userData as AppUser);
      return true; // User was newly created
    } else {
      // Non-destructive update: only refresh basic identity fields
      const updates: any = {};
      if (user.email) updates.email = user.email;
      if (user.displayName) updates.displayName = user.displayName;
      if ((user as any).photoURL) updates.photoURL = (user as any).photoURL;
      if (Object.keys(updates).length > 0) {
        await updateDoc(userDocRef, updates);
      }
      // Keep existing languages/level/completedTour intact
      const existing = snap.data() as AppUser;
      this.currentUserSubject.next(existing);
      return false; // User already existed
    }
  }

  private async ensureUserDoc(uid: string, { email, displayName, nativeLang }:
    { email: string | null, displayName: string, nativeLang: string }): Promise<boolean> {
    const userDocRef = doc(this.firestore, `users/${uid}`);
    const snap = await this.safeGetDoc(userDocRef);
    if (!snap || !snap.exists()) {
      const userData = {
        uid,
        email: email ?? null,
        displayName,
        nativeLanguage: nativeLang || 'en',
        targetLanguage: '',
        level: {},
        photoURL: '',
        bookmarks: [],
        completedTour: false,
        audioSpeed: 2,
        femaleVoice: true
      };
      await this.safeSetDoc(userDocRef, userData, true);
      this.currentUserSubject.next(userData as AppUser);
      return true; // User was newly created
    } else {
      // Do not overwrite existing user profile fields here
      const existing = snap.data() as AppUser;
      // Optionally refresh identity fields non-destructively
      const updates: any = {};
      if (email && email !== existing.email) updates.email = email;
      if (displayName && displayName !== existing.displayName) updates.displayName = displayName;
      if (Object.keys(updates).length > 0) {
        await updateDoc(userDocRef, updates);
      }
      this.currentUserSubject.next({ ...existing, ...updates });
      return false; // User already existed
    }
  }

  private async waitForBridgedUser(expectedUid: string, timeoutMs: number = 15000): Promise<FirebaseUser> {
    const start = Date.now();
    let lastLog = 0;
    while (Date.now() - start < timeoutMs) {
      const jsUser = this.auth.currentUser;
      if (jsUser && jsUser.uid === expectedUid) {
        try { await jsUser.getIdToken(true); } catch {}
        return jsUser;
      }
      if (Date.now() - lastLog > 1000) {
        console.log('[AuthDebug] waiting for JS bridge...', { expectedUid, currentJsUid: jsUser?.uid || null });
        lastLog = Date.now();
      }
      await new Promise((r) => setTimeout(r, 150));
    }
    throw new Error('JS Auth bridge timeout');
  }

  private async bridgeWebAuthIfNeeded(expectedUid: string, performWebSignIn: () => Promise<void>): Promise<void> {
    const jsUser = this.auth.currentUser;
    if (!jsUser || jsUser.uid !== expectedUid) {
      console.log('[AuthDebug] bridging web auth...', { expectedUid, currentJsUid: jsUser?.uid || null });
      await performWebSignIn();
      await this.waitForBridgedUser(expectedUid);
    }
  }

  private async waitForAuthToken(timeoutMs: number = 10000): Promise<void> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const jsUser = this.auth.currentUser;
      if (jsUser) {
        try {
          await jsUser.getIdToken(true);
          return;
        } catch {}
      }
      await new Promise((r) => setTimeout(r, 150));
    }
    throw new Error('Timed out waiting for auth token');
  }

  private async waitForNoJsUser(timeoutMs: number = 5000): Promise<void> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (!this.auth.currentUser) return;
      await new Promise((r) => setTimeout(r, 100));
    }
    console.warn('[AuthDebug] waitForNoJsUser timeout; proceeding');
  }

  private async safeGetDoc(ref: any): Promise<any | null> {
    try {
      return await getDoc(ref);
    } catch (e: any) {
      if (e?.code === 'permission-denied') {
        await this.waitForAuthToken();
        try {
          return await getDoc(ref);
        } catch (e2) {
          console.warn('safeGetDoc permission-denied after retry:', e2);
          return null;
        }
      }
      throw e;
    }
  }

  private async safeSetDoc(ref: any, data: any, merge: boolean = false): Promise<void> {
    const options: SetOptions = merge ? { merge: true } : {} as SetOptions;
    try {
      await setDoc(ref, data, options);
    } catch (e: any) {
      if (e?.code === 'permission-denied') {
        await this.waitForAuthToken();
        try {
          await setDoc(ref, data, options);
        } catch (e2) {
          console.warn('safeSetDoc permission-denied after retry:', e2);
          throw e2;
        }
      } else {
        throw e;
      }
    }
  }

  // **Update User Document in Firestore**
  async updateUserDocument(uid: string, updates: Partial<AppUser>): Promise<void> {
    const userDoc = doc(this.firestore, `users/${uid}`);
    await updateDoc(userDoc, updates);
  }

  // Generate funny display name
  async generateDisplayName(): Promise<string> {
    const adjectives = ['Quick', 'Lazy', 'Happy', 'Sad', 'Curious', 'Brave', 'Clever', 'Silly'];
    const nouns = ['Fox', 'Dog', 'Cat', 'Bird', 'Fish', 'Lion', 'Tiger', 'Bear', 'Monkey', 'Panda'];
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    const randomNumber = Math.floor(Math.random() * 1000);
    return `${randomAdjective} ${randomNoun} ${randomNumber}`;
  }

  async updateBookmarks(uid: string, articleId: string): Promise<void> {
    const userDoc = doc(this.firestore, `users/${uid}`);
    const userSnap = await getDoc(userDoc);
    if (userSnap.exists()) {
      const userData = userSnap.data() as AppUser;
      const updatedBookmarks = userData.bookmarks.includes(articleId)
        ? userData.bookmarks.filter(id => id !== articleId)
        : [...userData.bookmarks, articleId];
      await updateDoc(userDoc, { bookmarks: updatedBookmarks });
    }
  }
}
